/** Styled components for SWOTView (split out of SWOTView.jsx, #1166). */
import styled from '@emotion/styled';

export const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 600px;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 4px;
`;

export const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#888'};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ============ GRID VIEW ============

export const GridContainer = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2px;
  padding: 20px 40px 40px 40px;
  position: relative;
`;

export const Quadrant = styled.div`
  background: ${props => props.$color}15;
  border: 1px solid ${props => props.$color}30;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$color}20;
  }
`;

export const QuadrantLabel = styled.div`
  position: absolute;
  top: 12px;
  ${props => props.$position === 'left' ? 'left: 12px;' : 'right: 12px;'}
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.$color};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  opacity: 0.8;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const QuadrantCount = styled.div`
  position: absolute;
  bottom: 12px;
  ${props => props.$position === 'left' ? 'left: 12px;' : 'right: 12px;'}
  color: ${props => props.$color};
  font-size: 2rem;
  font-weight: 300;
  opacity: 0.3;
`;

export const ItemsContainer = styled.div`
  position: absolute;
  top: 40px;
  left: 10px;
  right: 10px;
  bottom: 40px;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 8px;
  padding: 8px;
  overflow-y: auto;
`;

export const ItemBubble = styled.div`
  background: ${props => props.$color}40;
  border: 1px solid ${props => props.$color}80;
  border-radius: ${props => Math.max(16, props.$size / 2)}px;
  padding: ${props => `${8 + props.$size * 0.1}px ${12 + props.$size * 0.15}px`};
  color: white;
  font-size: ${props => Math.max(0.7, Math.min(0.9, 0.7 + props.$size * 0.01))}rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${props => props.$color}60;
    transform: scale(1.05);
    z-index: 10;
  }
`;

export const CenterLines = styled.div`
  position: absolute;
  top: 20px;
  left: 40px;
  right: 40px;
  bottom: 40px;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(to bottom,
      transparent,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.2) 80%,
      transparent
    );
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 2px;
    background: linear-gradient(to right,
      transparent,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.2) 80%,
      transparent
    );
  }
`;

export const AxisLabels = styled.div`
  position: absolute;
  pointer-events: none;
`;

export const XAxisLabel = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  display: flex;
  align-items: center;
  gap: 40px;

  span:first-of-type { color: #F44336; }
  span:last-of-type { color: #4CAF50; }
`;

export const YAxisLabel = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 40px;

  span:first-of-type { color: #FF9800; }
  span:last-of-type { color: #2196F3; }
`;

// ============ PIE CHART VIEW ============

export const PieContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 60px;
  padding: 40px;
`;

export const PieWrapper = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
`;

export const PieLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const OptimismScoreCard = styled.div`
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 24px 32px;
  text-align: center;
  min-width: 220px;
`;

export const OptimismEmoji = styled.div`
  font-size: 3rem;
  margin-bottom: 8px;
`;

export const OptimismLabel = styled.div`
  color: #888;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 8px;
`;

export const OptimismValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.$color};
  line-height: 1;
  margin-bottom: 8px;
`;

export const OptimismDescription = styled.div`
  color: #aaa;
  font-size: 0.85rem;
  line-height: 1.4;
`;

export const OptimismBar = styled.div`
  width: 100%;
  height: 8px;
  background: linear-gradient(to right, #F44336, #FF9800, #FFC107, #8BC34A, #4CAF50);
  border-radius: 4px;
  margin: 16px 0 8px 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: ${props => props.$position}%;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    transform: translateX(-50%);
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  }
`;

// ============ BAR CHART VIEW ============

export const BarContainer = styled.div`
  flex: 1;
  padding: 40px 60px;
  overflow-y: auto;
`;

export const BarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const BarLabel = styled.div`
  width: 200px;
  font-size: 0.85rem;
  color: #ccc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const BarTrack = styled.div`
  flex: 1;
  height: 24px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
`;

export const BarCenterLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  background: rgba(255, 255, 255, 0.3);
`;

export const BarFill = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  border-radius: 3px;
  background: ${props => props.$color};
  transition: all 0.3s ease;
`;

export const BarValue = styled.div`
  width: 60px;
  text-align: right;
  font-size: 0.8rem;
  color: ${props => props.$color};
  font-weight: 500;
`;

// ============ SCATTER PLOT VIEW ============

export const ScatterContainer = styled.div`
  flex: 1;
  position: relative;
  margin: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
`;

export const ScatterPoint = styled.div`
  position: absolute;
  width: ${props => Math.max(20, props.$size * 2)}px;
  height: ${props => Math.max(20, props.$size * 2)}px;
  border-radius: 50%;
  background: ${props => props.$color}80;
  border: 2px solid ${props => props.$color};
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  color: white;
  font-weight: 600;

  &:hover {
    transform: translate(-50%, -50%) scale(1.3);
    z-index: 100;
    background: ${props => props.$color};
  }
`;

export const ScatterAxisLabel = styled.div`
  position: absolute;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.7rem;
  letter-spacing: 0.05em;
`;

export const ScatterGridLine = styled.div`
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
`;

// ============ LIST VIEW ============

export const ListContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

export const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

export const ListHeader = styled.th`
  text-align: left;
  padding: 12px 16px;
  color: #888;
  font-weight: 500;
  border-bottom: 1px solid #333;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: #fff;
  }

  svg {
    width: 14px;
    height: 14px;
    margin-left: 4px;
    vertical-align: middle;
  }
`;

export const ListRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid #222;
    color: #ccc;
  }
`;

export const QuadrantBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => props.$color}30;
  color: ${props => props.$color};
`;

export const SentimentIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .bar {
    width: 60px;
    height: 6px;
    background: #222;
    border-radius: 3px;
    position: relative;
    overflow: hidden;
  }

  .fill {
    position: absolute;
    top: 0;
    height: 100%;
    border-radius: 3px;
  }
`;

// ============ MODAL ============

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: #0a0a0a;
  border: 2px solid ${props => props.$color || '#333'};
  border-radius: 8px;
  padding: 24px 32px 32px 32px;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  color: #fff;
  position: relative;
`;

export const ModalCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: 1px solid #555;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #888;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$color || '#5B9DD9'};
    border-color: ${props => props.$color || '#5B9DD9'};
    color: #fff;
  }
`;

export const ModalTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${props => props.$color || '#fff'};
  padding-right: 50px;
`;

export const ModalQuadrant = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${props => props.$color}30;
  border: 1px solid ${props => props.$color};
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.$color};
  margin-bottom: 16px;
`;

export const ModalReasoning = styled.p`
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #aaa;
`;

export const ModalSection = styled.div`
  background: #111;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 12px;
`;

export const ModalSectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const ExampleList = styled.ul`
  margin: 0;
  padding: 0 0 0 16px;
  font-size: 0.85rem;
  color: #ccc;

  li {
    margin-bottom: 4px;
  }
`;

export const ScoreBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .label {
    width: 80px;
    font-size: 0.75rem;
    color: #888;
  }

  .bar {
    flex: 1;
    height: 8px;
    background: #222;
    border-radius: 4px;
    position: relative;
    overflow: hidden;
  }

  .fill {
    position: absolute;
    top: 0;
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .value {
    width: 40px;
    text-align: right;
    font-size: 0.75rem;
    color: #888;
  }
`;

export const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #333;
  font-size: 0.8rem;
  color: #888;

  span {
    color: #fff;
    font-weight: 500;
  }
`;
